import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, limit, orderBy, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useSession } from "../lib/auth";
import { useCollection } from "../hooks/useCollection";
import { useCallableAction } from "../hooks/useCallableAction";
import {
  DateRangePicker,
  type RangeValue,
  type RangePreset,
} from "../components/DateRangePicker";
import { tl, n, pct, date } from "../lib/format";
import { LoadingBlock } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { OrderStatusBadge } from "../components/OrderStatusBadge";
import { useProductionStageLabels } from "../hooks/useProductionStageLabels";

/**
 * Dashboard.
 *
 * YETKİ (04-yetki-matrisi §6 + §2.2):
 * · Operasyonel kartlar → üç rol
 * · CİRO paneli        → üç rol   (satış tutarı zaten Shopify panelinde açık)
 * · KÂR paneli         → yalnızca financeView (boss)
 *
 * G-08'in kapandığı yer: eski `ajax_financialdashboard.php` rol kontrolü
 * yapmıyordu; finansal panel sadece HTML'de gizleniyordu, uç nokta herkese
 * açıktı. Artık kâr metriği ayrı dokümanda ve sorgu bile açılmıyor.
 */

interface MetricDoc {
  sensitivity: "ops" | "revenue" | "profit";
  label?: string;
  /** Para alanları KURUŞ cinsindendir (CONTRACT: Firestore'da her zaman kuruş). */
  value?: number;
  kind?: "currency" | "count" | "percent" | "text";
  text?: string;
  unit?: string;
  period?: string;
  href?: string;
  sortOrder?: number;
  /**
   * ★ ÖLÇÜLEMEDİ ≠ SIFIR.
   *
   * Sunucu, sayımı patlayan ya da tabanı boş olan metriğe `value` YAZMAZ ve bu
   * bayrağı kaldırır. Eskiden böyle bir metrik 0 geliyordu ve panoda
   * "Açık üretim: 0" yazarken atölyede 19 iş vardı — gerçek 0 ile ölçülemeyen 0
   * ekranda birbirinden ayırt edilemiyordu.
   */
  unmeasured?: boolean;
  /** Rakamın HANGİ TABANDAN hesaplandığı. Oranların altında yazdırılır. */
  basis?: string;
}

interface OrderRow {
  orderName?: string;
  customerFullName?: string;
  erpStatus?: string;
  erpStatusLabel?: string;
  productionStatus?: string;
  totals?: { total?: number };
  createdAt?: { toDate: () => Date } | null;
}

function metricText(m: MetricDoc): string {
  // ★ Ölçülemeyen metrik SAYI GİBİ BASILMAZ; sebebi yazılır.
  if (m.unmeasured) return m.text ?? "ölçülemedi";
  switch (m.kind) {
    case "currency":
      return tl(m.value);
    case "percent":
      return pct(m.value);
    case "count":
      return n(m.value, m.unit ? ` ${m.unit}` : " adet");
    case "text":
      return m.text ?? "—";
    default:
      return m.text ?? n(m.value, m.unit ? ` ${m.unit}` : "");
  }
}

/** Öne çıkan metrik — koyu kartla vurgulanır (dönemin cirosu). */
const HERO_METRICS = new Set(["revenue_month", "gross_profit_month"]);

function MetricCard({ m }: { m: MetricDoc & { id: string } }) {
  const hero = HERO_METRICS.has(m.id) && !m.unmeasured;
  const body = (
    <>
      <dt className={`truncate text-[13px] font-medium ${hero ? "text-primary-200" : "text-gray-500"}`}>
        {m.label ?? m.id}
      </dt>
      <dd
        className={
          m.unmeasured
            ? "mt-1 text-base font-medium italic tracking-tight text-amber-700"
            : `mt-1 text-[26px] font-bold tracking-tight tabular-nums ${hero ? "text-white" : "text-gray-950"}`
        }
      >
        {metricText(m)}
      </dd>
      {m.period ? <p className={`mt-1 text-xs ${hero ? "text-gray-400" : "text-gray-500"}`}>{m.period}</p> : null}
      {/*
        ★ EKRANDA GÖSTERİLEN HER ORANIN TABANI YAZAR.
        "%55,5" tek başına, neyin yüzdesi olduğu söylenmeden yanıltıcıdır:
        kâr oranı TÜM ciroya değil, yalnızca maliyeti tam bilinen siparişlerin
        net hasılatına bölünür.
      */}
      {m.basis && !m.unmeasured ? (
        /* ★ 11px telefonda okunmuyordu, text-gray-400 kontrastı da düşüktü.
           En küçük ölçü text-xs (12px), renk text-gray-500. */
        <p className={`mt-1 text-xs leading-snug ${hero ? "text-gray-400" : "text-gray-500"}`}>Taban: {m.basis}</p>
      ) : null}
    </>
  );

  if (m.href && m.href.startsWith("/")) {
    return (
      <Link
        to={m.href}
        className="card card-pad block transition hover:-translate-y-0.5 hover:shadow-md hover:ring-primary-300"
      >
        {body}
      </Link>
    );
  }
  return (
    <div
      className={
        hero
          ? "relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-950 via-gray-900 to-primary-950 p-4 shadow-lg ring-1 ring-gray-900 sm:p-5"
          : "card card-pad"
      }
    >
      {body}
    </div>
  );
}

interface MetricsResponse {
  preset: RangePreset;
  rangeLabel: string;
  presetLabel: string;
  days: number;
  canSeeProfit: boolean;
  /**
   * Ciro bloğu gösterilsin mi? Sunucu `costView` claim'ine göre karar verir ve
   * `revenue` metriklerini yetkisiz role HİÇ GÖNDERMEZ. Ekran rolü kendi
   * yorumlamaz; sunucunun söylediğini uygular.
   */
  canSeeRevenue?: boolean;
  metrics: Array<MetricDoc & { id: string }>;
}

/** Seçilen aralık oturumlar arası korunur — her açılışta yeniden seçmek gerekmez. */
const STORAGE_KEY = "balder.dashboard.range";

function loadRange(): RangeValue {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const v = JSON.parse(raw) as RangeValue;
      if (v && typeof v.preset === "string") return v;
    }
  } catch {
    // Bozuk kayıt panelin açılmasını engellemez.
  }
  return { preset: "this_month" };
}

export function Dashboard() {
  const session = useSession();

  const [range, setRange] = useState<RangeValue>(loadRange);
  const [data, setData] = useState<MetricsResponse | null>(null);

  /**
   * ★ METRİKLER SUNUCUDA HESAPLANIR, ÖNCEDEN YAZILMIŞ DOKÜMANDAN OKUNMAZ.
   *
   * Önceden hesaplanan `metrics/*` dokümanları yalnızca "Bu ay"ı bilir; serbest
   * tarih aralığı onlardan türetilemez. Uç ayrıca kâr metriklerini `financeView`
   * yoksa HİÇ ÜRETMEZ — arayüzde gizlemek yeterli değildir, veri sunucudan
   * çıkmamalıdır (eski sistemin G-08 hatası tam olarak buydu).
   */
  const load = useCallableAction<
    { preset: string; from?: string; to?: string },
    MetricsResponse
  >("getDashboardMetrics");

  const run = useCallback(
    (v: RangeValue) => {
      load
        .run({ preset: v.preset, from: v.from, to: v.to })
        .then((res) => { if (res) setData(res); })
        .catch(() => undefined);
    },
    [load],
  );

  useEffect(() => {
    run(range);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(range));
    } catch {
      // Depolama kapalıysa panel yine çalışır.
    }
    // `run` her render'da yeniden üretilmesin diye bağımlılık yalnızca aralıktır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.preset, range.from, range.to]);

  const stages = useProductionStageLabels();
  const recent = useCollection<OrderRow>(
    () => query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(8)),
    [],
  );

  const all = data?.metrics ?? [];
  const sorted = [...all].sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100));
  const ops = sorted.filter((m) => m.sensitivity === "ops");
  const revenue = sorted.filter((m) => m.sensitivity === "revenue");
  const profit = sorted.filter((m) => m.sensitivity === "profit");
  const seeFinance = data?.canSeeProfit ?? false;
  /**
   * ★ CİRO ARTIK ÜRETİM ROLÜNE KAPALI (iş sahibi kararı: ciro atölyenin işi
   * değil). Eski bayrak yoksa `revenue` dizisinin dolu olup olmadığına bakılır
   * — sunucu yetkisiz role o metrikleri zaten göndermiyor, yani eski bir
   * sürümle de blok yanlışlıkla açılmaz.
   */
  const seeRevenue = data?.canSeeRevenue ?? revenue.length > 0;
  const busy = load.loading;
  const metrics = { loading: busy && !data, error: load.error, docs: all };

  return (
    <div className="space-y-8 animate-fade-in">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">Panel</h1>
          <p className="mt-1 text-sm text-gray-600">
            Hoş geldiniz{session?.displayName ? `, ${session.displayName}` : ""}.
          </p>
        </div>
        <div className="shrink-0">
          <DateRangePicker
            value={range}
            onChange={setRange}
            resolvedLabel={data?.rangeLabel ?? null}
            busy={busy}
          />
        </div>
      </header>

      {metrics.error ? (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
          {metrics.error}
        </div>
      ) : null}

      {/* ── CİRO — boss + office. Üretim rolü GÖRMEZ. ────────────────────── */}
      {seeRevenue ? (
      <section aria-labelledby="ciro-baslik">
        <h2 id="ciro-baslik" className="section-heading">
          Ciro
        </h2>
        {metrics.loading ? (
          <LoadingBlock />
        ) : revenue.length === 0 ? (
          <div className="mt-3">
            <EmptyState
              title="Ciro özeti henüz hesaplanmadı"
              description="Özet metrikleri toplayan görev ilk çalıştığında burada görünecek."
            />
          </div>
        ) : (
          <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {revenue.map((m) => (
              <MetricCard key={m.id} m={m} />
            ))}
          </dl>
        )}
      </section>
      ) : null}

      {/* ── KÂR — YALNIZCA financeView ───────────────────────────────────── */}
      {seeFinance ? (
        <section aria-labelledby="kar-baslik">
          <div className="flex items-center gap-2">
            <h2 id="kar-baslik" className="section-heading">
              Kârlılık
            </h2>
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold uppercase text-amber-900">
              Gizli
            </span>
          </div>
          {profit.length === 0 ? (
            <div className="mt-3">
              <EmptyState title="Kâr özeti henüz hesaplanmadı" />
            </div>
          ) : (
            <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {profit.map((m) => (
                <MetricCard key={m.id} m={m} />
              ))}
            </dl>
          )}

          {/*
            ★ RAPOR DÜĞMELERİ `financeView` DEĞİL, "boss + financeView" İSTER.

            Bu blok `seeFinance` (yalnızca `financeView`) ile açılıyordu, oysa
            rapor uçları İKİ koşulu birden arıyor
            (`reports/profit.ts:124-126`). `financeView` verilmiş bir ofis
            kullanıcısı kâr kartlarını haklı olarak görüyor ama bu iki düğme
            onu 403 paneline götürüyordu. Kartlar kalır, GİDİLEMEYEN düğme
            gösterilmez.
          */}
          {session?.role === "boss" ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/reports/profit" className="btn-secondary">
                Ürün Kâr Tablosu (Excel)
              </Link>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* ── OPERASYON ────────────────────────────────────────────────────── */}
      <section aria-labelledby="ops-baslik">
        <h2 id="ops-baslik" className="section-heading">
          Operasyon
        </h2>
        {metrics.loading ? (
          <LoadingBlock />
        ) : ops.length === 0 ? (
          <div className="mt-3">
            <EmptyState
              title="Operasyon özeti henüz hesaplanmadı"
              description="Geciken üretim, açık SSH ve kritik stok sayıları burada listelenecek."
            />
          </div>
        ) : (
          <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {ops.map((m) => (
              <MetricCard key={m.id} m={m} />
            ))}
          </dl>
        )}
      </section>

      {/* ── SON SİPARİŞLER ───────────────────────────────────────────────── */}
      <section aria-labelledby="son-siparis-baslik">
        <div className="flex items-center justify-between">
          <h2
            id="son-siparis-baslik"
            className="section-heading"
          >
            Son siparişler
          </h2>
          <Link to="/orders" className="link text-sm">
            Tümü
          </Link>
        </div>

        {recent.loading ? (
          <LoadingBlock />
        ) : recent.error ? (
          <div role="alert" className="mt-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
            {recent.error}
          </div>
        ) : recent.docs.length === 0 ? (
          <div className="mt-3">
            <EmptyState title="Henüz sipariş yok" />
          </div>
        ) : (
          <ul role="list" className="mt-3 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
            {recent.docs.map((o) => (
              <li key={o.id}>
                <Link
                  to={`/orders/${o.id}`}
                  className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-primary-50"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      <span className="mr-2 text-gray-500">{o.orderName ?? o.id}</span>
                      {o.customerFullName ?? "—"}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">{date(o.createdAt)}</p>
                  </div>
                  {/* Telefonda en fazla yarı genişlik — uzun aşama rozeti müşteri adını
                      silmesin (bkz. Search.tsx'teki aynı satır). */}
                  <div className="flex max-w-[50%] shrink-0 flex-col items-end gap-1 sm:max-w-none sm:flex-row sm:items-center sm:gap-3">
                    <OrderStatusBadge
                      erpStatus={o.erpStatus}
                      erpStatusLabel={o.erpStatusLabel}
                      productionStatus={o.productionStatus}
                      stages={stages}
                    />
                    {/* Sipariş tutarı üç role de açıktır (yetki matrisi §2.2). */}
                    <span className="text-sm tabular-nums text-gray-900">{tl(o.totals?.total)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default Dashboard;
